import React from 'react';
import { ControlObligation, StatutoryProvision } from '../types/obligation';
import { ApplicabilityDecision, DecisionStatus } from '../types/decision';
import { ArchitectureSystem } from '../types/system';
import { formatCitation, getTrancheInfo } from '../utils/citationFormatter';
import { getGovernmentLinkage } from '../utils/governmentDocumentLookup';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  HelpCircle,
  ChevronRight,
  UserCheck,
  Cpu,
  FileText,
  AlertTriangle,
  Scale,
  Layers,
  Server,
  Landmark,
  ExternalLink,
  Shield
} from 'lucide-react';

interface ObligationCardProps {
  item: ControlObligation | StatutoryProvision;
  isControl: boolean;
  decision?: ApplicabilityDecision;
  customSystems?: ArchitectureSystem[];
  onQuickDecision: (targetId: string, status: DecisionStatus) => void;
  onOpenDrawer: (item: ControlObligation | StatutoryProvision, isControl: boolean) => void;
}

export default function ObligationCard({
  item,
  isControl,
  decision,
  customSystems = [],
  onQuickDecision,
  onOpenDrawer
}: ObligationCardProps) {
  const currentStatus: DecisionStatus = decision?.status || 'UNASSESSED';

  // Common metadata
  const id = isControl ? (item as ControlObligation).id : (item as StatutoryProvision).id;
  const title = isControl
    ? (item as ControlObligation).title || (item as ControlObligation).name
    : formatCitation((item as StatutoryProvision).provision);
  const scope = isControl
    ? (item as ControlObligation).scope || (item as ControlObligation).provision
    : (item as StatutoryProvision).actor;
  const commencement = isControl
    ? (item as ControlObligation).legal_source_records?.[0]?.commencement || '2027-05-13'
    : (item as StatutoryProvision).commencement;
  const tranche = getTrancheInfo(commencement);
  const owner = decision?.assigned_owner || (isControl ? (item as ControlObligation).recommended_owner : 'Unassigned');
  
  // Government Published Document Linkage
  const govLinkage = getGovernmentLinkage(item, isControl);

  // Custom mapped systems vs default
  const mappedSystemsList: string[] = decision?.systems_in_scope && decision.systems_in_scope.length > 0
    ? decision.systems_in_scope
    : (isControl && (item as ControlObligation).systems ? (item as ControlObligation).systems.split(';').map(s => s.trim()).filter(Boolean) : []);

  return (
    <div className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
      currentStatus === 'APPLICABLE'
        ? 'bg-white dark:bg-slate-900 border-emerald-500/40 hover:border-emerald-500 shadow-xs'
        : currentStatus === 'NOT_APPLICABLE'
        ? 'bg-slate-50/80 dark:bg-slate-900/60 border-slate-200 dark:border-slate-800 opacity-80 hover:opacity-100'
        : currentStatus === 'UNRESOLVED'
        ? 'bg-amber-50/40 dark:bg-amber-950/20 border-amber-400 dark:border-amber-700/60 shadow-xs'
        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 shadow-xs'
    }`}>
      
      {/* Top Banner / ID & Tranche */}
      <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-start justify-between gap-3 border-b border-slate-100 dark:border-slate-800/80">
        
        <div className="space-y-1.5 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            
            {/* Primary ID Badge */}
            <span className={`px-2.5 py-0.5 rounded-lg text-xs font-mono font-extrabold border ${
              isControl
                ? 'bg-indigo-500/10 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 border-indigo-500/30'
                : 'bg-accent/10 dark:bg-accent/20 text-accent dark:text-accent-light border-accent/30'
            }`}>
              {id}
            </span>

            {/* Tranche Badge */}
            <span className={`px-2 py-0.5 rounded-md text-[10px] font-mono font-bold border ${tranche.badgeClass}`}>
              {tranche.tranche} ({tranche.date})
            </span>

            {/* Scope / Actor */}
            <span className="text-xs font-mono text-slate-500 dark:text-slate-400">
              {scope}
            </span>

            {/* Government Published Document Badge */}
            <a
              href={govLinkage.officialUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 transition-colors shrink-0"
              title={`${govLinkage.officialTitle} (${govLinkage.gazetteNotification}) • Published ${govLinkage.instrumentDate} • SHA-256: ${govLinkage.sha256.slice(0, 12)}... (Click to view official Govt PDF)`}
              onClick={(e) => e.stopPropagation()}
            >
              <Landmark className="w-3 h-3 text-accent" />
              <span>{govLinkage.shortTitle}</span>
              <ExternalLink className="w-2.5 h-2.5 opacity-60" />
            </a>

            {/* Statutory Exception Safe Harbour Badge */}
            {currentStatus === 'NOT_APPLICABLE' && (decision?.rationale?.toLowerCase().includes('exempt') || decision?.entity_facts?.toLowerCase().includes('statutory exception')) && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-800"
                title={decision?.rationale || 'Exempt under Section 17 / 3(c) statutory safe harbour'}
              >
                <Shield className="w-3 h-3 text-amber-600" />
                <span>Safe Harbour</span>
              </span>
            )}

          </div>

          <h4 className="text-sm sm:text-base font-bold text-slate-900 dark:text-slate-100 tracking-tight leading-snug">
            {title}
          </h4>

          {/* Description snippet */}
          <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2 leading-relaxed">
            {isControl
              ? (item as ControlObligation).description || (item as ControlObligation).control
              : (item as StatutoryProvision).exact_passage?.slice(0, 180) + '...'}
          </p>
        </div>

        {/* Status Pill */}
        <div className="flex sm:flex-col items-end gap-2 shrink-0">
          <span className={`px-3 py-1 rounded-full text-xs font-bold tracking-wide uppercase border flex items-center gap-1.5 ${
            currentStatus === 'APPLICABLE'
              ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30'
              : currentStatus === 'NOT_APPLICABLE'
              ? 'bg-slate-200/80 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-700'
              : currentStatus === 'UNRESOLVED'
              ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30'
              : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700'
          }`}>
            {currentStatus === 'APPLICABLE' && <CheckCircle2 className="w-3.5 h-3.5" />}
            {currentStatus === 'NOT_APPLICABLE' && <XCircle className="w-3.5 h-3.5" />}
            {currentStatus === 'UNRESOLVED' && <AlertCircle className="w-3.5 h-3.5" />}
            {currentStatus === 'UNASSESSED' && <HelpCircle className="w-3.5 h-3.5" />}
            <span>{currentStatus.replace('_', ' ')}</span>
          </span>
        </div>

      </div>

      {/* Bottom Bar: Quick Tri-State Toggles + Owner + Drawer Trigger */}
      <div className="p-3 sm:px-5 bg-slate-50/50 dark:bg-slate-900/40 flex flex-wrap items-center justify-between gap-3 text-xs">
        
        {/* Quick Decision Action Buttons */}
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-semibold text-slate-400 dark:text-slate-500 mr-1 hidden sm:inline">
            Quick Decision:
          </span>

          <button
            onClick={() => onQuickDecision(id, 'APPLICABLE')}
            className={`px-2.5 py-1 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors border ${
              currentStatus === 'APPLICABLE'
                ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-emerald-400 dark:hover:border-emerald-500'
            }`}
            title="Mark as in-scope and applicable"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>Applicable</span>
          </button>

          <button
            onClick={() => onQuickDecision(id, 'NOT_APPLICABLE')}
            className={`px-2.5 py-1 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors border ${
              currentStatus === 'NOT_APPLICABLE'
                ? 'bg-slate-600 text-white border-slate-600 shadow-xs'
                : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500'
            }`}
            title="Mark as exempt or out of scope"
          >
            <XCircle className="w-3.5 h-3.5" />
            <span>Exempt</span>
          </button>

          <button
            onClick={() => onQuickDecision(id, 'UNRESOLVED')}
            className={`px-2.5 py-1 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors border ${
              currentStatus === 'UNRESOLVED'
                ? 'bg-amber-600 text-white border-amber-600 shadow-xs'
                : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-amber-400 dark:hover:border-amber-500'
            }`}
            title="Mark as unresolved / requiring legal review"
          >
            <AlertCircle className="w-3.5 h-3.5" />
            <span>Unresolved</span>
          </button>
        </div>

        {/* Right side: Owner + Architecture + Open Drawer */}
        <div className="flex items-center gap-3">
          
          {/* Owner pill */}
          <div className="hidden lg:flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
            <UserCheck className="w-3.5 h-3.5 text-slate-400" />
            <span className="font-medium text-[11px] truncate max-w-[140px]" title={owner}>
              {owner}
            </span>
          </div>

          {/* Mapped Architecture systems */}
          {mappedSystemsList.length > 0 && (
            <div className="hidden xl:flex items-center gap-1">
              {mappedSystemsList.slice(0, 2).map((sysId) => (
                <span
                  key={sysId}
                  className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-900/40 whitespace-nowrap shrink-0"
                  title={customSystems.find(s => s.id === sysId)?.name || sysId}
                >
                  {sysId}
                </span>
              ))}
              {mappedSystemsList.length > 2 && (
                <span className="text-[10px] text-slate-400 font-mono">
                  +{mappedSystemsList.length - 2}
                </span>
              )}
            </div>
          )}

          {/* Inspect / Decision Form Button */}
          <button
            onClick={() => onOpenDrawer(item, isControl)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-accent dark:text-accent-light bg-accent/10 hover:bg-accent/20 dark:bg-accent/20 dark:hover:bg-accent/30 border border-accent/30 transition-colors"
          >
            <span>Inspect & Scope</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </button>

        </div>

      </div>

    </div>
  );
}
